import { allocatedSolarElectrical } from './solarElectricalTopology';
import { z } from 'zod/v4';
import { pvWattsSnapshotSchema, type PvWattsSnapshot } from './solarPvWatts';
import { type SolarDesignDraftState } from './solarDesignDraft';
import {
  solarCommittedFace,
  solarCommittedFaceFingerprint,
  solarCommittedInputsFingerprint,
  type SolarCommittedFace,
} from './solarCommittedInputs';

export const solarWeatherGenerationSchema = z.discriminatedUnion('status', [
  z.object({
    version: z.literal(1),
    source: z.literal('pvwatts_v8'),
    status: z.literal('valid'),
    stateFingerprint: z.string().max(1_000_000),
    snapshot: pvWattsSnapshotSchema,
  }),
  z.object({
    version: z.literal(1),
    source: z.literal('pvwatts_v8'),
    status: z.literal('needs_recalculation'),
    stateFingerprint: z.string().max(1_000_000),
    snapshot: pvWattsSnapshotSchema,
  }),
  z.object({
    version: z.literal(1),
    source: z.literal('pvwatts_v8'),
    status: z.literal('unavailable'),
    reason: z.enum([
      'missing_configuration',
      'invalid_inputs',
      'rate_limited',
      'request_failed',
      'invalid_response',
      'cache_unavailable',
      'unsupported_design',
    ]),
  }),
]);
export type SolarWeatherGeneration = z.infer<
  typeof solarWeatherGenerationSchema
>;

/** Display this only while weather output is unavailable. */
export const solarWeatherUnavailableMessage = (
  result?: SolarWeatherGeneration
): string | null => {
  if (!result) return null;
  if (result.status !== 'unavailable')
    return 'PVWatts weather estimate needs recalculation for the current inputs.';
  const reasons: Record<typeof result.reason, string> = {
    missing_configuration:
      'PVWatts is unavailable: the server API key is not configured.',
    unsupported_design:
      'PVWatts requires a supported georeferenced design, confirmed roof planes and matching catalogue modules.',
    invalid_inputs:
      'The saved inputs cannot be used for a PVWatts weather estimate.',
    rate_limited:
      'PVWatts is rate limiting requests. Try Calculate again later.',
    request_failed:
      'The PVWatts weather service did not return a usable response. Try Calculate again later.',
    invalid_response:
      'The PVWatts weather response could not be verified. No generation result has been published.',
    cache_unavailable:
      'PVWatts weather calculation is temporarily unavailable: its source cache could not be used.',
  };
  return reasons[result.reason];
};

/** All physical and inclusion inputs, never historical per-panel output or result certification. */
export const solarWeatherStateFingerprint = (
  state: SolarDesignDraftState
): string => {
  const topology = allocatedSolarElectrical(state.electrical);
  return solarCommittedInputsFingerprint({
    ...(topology?.placementGroups.some(
      (group) =>
        topology.equipmentSelections.find(
          (selection) => selection.id === group.equipmentSelectionId
        )?.applianceId !== state.selectedPanelApplianceId
    )
      ? {
          panelEquipment: state.layout.workingPanels.map((panel) => [
            panel.id,
            topology.equipmentSelections.find(
              (selection) => selection.id === panel.equipmentSelectionId
            )?.applianceId ?? null,
          ]),
        }
      : {}),
    selectedPanelApplianceId: state.selectedPanelApplianceId,
    selectedPanelIndexes: state.selectedPanelIndexes,
    layout: {
      manualMode: state.layout.manualMode,
      workingObstacles: state.layout.workingObstacles,
      workingFaces: state.layout.workingFaces
        .filter((face) =>
          state.layout.workingPanels.some(
            (panel) =>
              panel.included &&
              panel.faceId === face.id &&
              !state.layout.excludedFaceIds.includes(face.id)
          )
        )
        .map((face) => ({
          id: face.id,
          pitchSource: face.pitchSource,
          ...solarCommittedFace(face),
        })),
      workingPanels: state.layout.workingPanels
        .filter(
          (panel) =>
            panel.included &&
            !state.layout.excludedFaceIds.includes(panel.faceId)
        )
        .map((panel) => ({
          faceId: panel.faceId,
          orientation: panel.orientation,
          corners: panel.corners,
        })),
    },
  });
};

export const currentSolarWeatherSnapshot = (
  state: SolarDesignDraftState
): PvWattsSnapshot | null => {
  const parsed = solarWeatherGenerationSchema.safeParse(
    state.weatherGeneration
  );
  const weather = parsed.success ? parsed.data : null;
  if (
    weather?.status !== 'valid' ||
    weather.stateFingerprint !== solarWeatherStateFingerprint(state)
  )
    return null;
  const module = weather.snapshot.capturedInputs.arrays[0]?.modules[0];
  if (
    !module ||
    weather.snapshot.capturedInputs.arrays.some((array) =>
      array.modules.some(
        (item) => item.moduleId !== state.selectedPanelApplianceId
      )
    )
  )
    return null;
  const geometry = {
    faces: state.layout.workingFaces,
    packedPanels: state.layout.workingPanels
      .filter(
        (panel) =>
          panel.included && !state.layout.excludedFaceIds.includes(panel.faceId)
      )
      .map((panel) => ({
        ...panel,
        faceIndex: state.layout.workingFaces.findIndex(
          (face) => face.id === panel.faceId
        ),
      })),
    panelDimensions: {
      widthMeters: module.widthMeters,
      heightMeters: module.heightMeters,
      capacityWatts: module.capacityWatts,
    },
  };
  return matchesSolarWeatherGeometry(weather.snapshot, geometry)
    ? weather.snapshot
    : null;
};

type FrozenGeometry = {
  faces: readonly SolarCommittedFace[];
  packedPanels: readonly {
    corners: unknown;
    orientation: string;
    faceIndex: number;
  }[];
  panelDimensions: {
    widthMeters: number;
    heightMeters: number;
    capacityWatts: number;
  };
  weatherGeneration?: {
    result: SolarWeatherGeneration;
    geometryFingerprint: string;
  };
};
export const solarWeatherGeometryFingerprint = (
  geometry: FrozenGeometry,
  indexes?: readonly number[] | null
): string =>
  solarCommittedInputsFingerprint({
    faces: geometry.faces.map(solarCommittedFace),
    panels: geometry.packedPanels.map((panel) => [
      panel.corners,
      panel.orientation,
      panel.faceIndex,
    ]),
    dimensions: geometry.panelDimensions,
    selection: indexes?.length ? indexes : null,
  });
export const frozenSolarWeatherSnapshot = (
  geometry: FrozenGeometry,
  indexes?: readonly number[] | null
): PvWattsSnapshot | null => {
  const weather = geometry.weatherGeneration;
  const parsed = solarWeatherGenerationSchema.safeParse(weather?.result);
  const result = parsed.success ? parsed.data : null;
  return result?.status === 'valid' &&
    weather != null &&
    weather.geometryFingerprint ===
      solarWeatherGeometryFingerprint(geometry, indexes) &&
    matchesSolarWeatherGeometry(result.snapshot, geometry, indexes)
    ? result.snapshot
    : null;
};

/** A conversion may reorder faces, but cannot change the modelled panel scope. */
export const matchesSolarWeatherGeometry = (
  snapshot: PvWattsSnapshot,
  geometry: FrozenGeometry,
  indexes?: readonly number[] | null
): boolean => {
  const selection = indexes?.length ? new Set(indexes) : null;
  const actual = geometry.packedPanels.filter(
    (_, index) => !selection || selection.has(index)
  );
  const expected = snapshot.capturedInputs.arrays.flatMap(
    (array) => array.modules
  );
  if (actual.length !== expected.length) return false;
  const panelKey = (panel: { corners: unknown; orientation: string }) =>
    JSON.stringify([panel.corners, panel.orientation]);
  const expectedKeys = new Set(expected.map(panelKey));
  if (
    expectedKeys.size !== expected.length ||
    new Set(actual.map(panelKey)).size !== actual.length ||
    actual.some((panel) => !expectedKeys.has(panelKey(panel)))
  )
    return false;
  const dimensions = geometry.panelDimensions;
  for (const array of snapshot.capturedInputs.arrays) {
    if (!array.committedFaceFingerprint) return false;
    const faces = geometry.faces.flatMap((face, index) =>
      JSON.stringify(face.polygon) === JSON.stringify(array.roofPolygon) &&
      face.pitchDegrees === array.appliedPlane.tilt &&
      ((face.azimuthDegrees % 360) + 360) % 360 ===
        array.appliedPlane.azimuth &&
      face.appliedArrayAzimuthDegrees !== null &&
      solarCommittedFaceFingerprint(face) === array.committedFaceFingerprint
        ? [index]
        : []
    );
    if (faces.length !== 1) return false;
    if (
      array.modules.some(
        (module) =>
          dimensions.widthMeters == null ||
          dimensions.heightMeters == null ||
          Math.abs(module.widthMeters - dimensions.widthMeters) > 0.001 ||
          Math.abs(module.heightMeters - dimensions.heightMeters) > 0.001 ||
          module.capacityWatts !== dimensions.capacityWatts
      )
    )
      return false;
    const keys = new Set(array.modules.map(panelKey));
    if (
      actual.filter((panel) => panel.faceIndex === faces[0]).length !==
        keys.size ||
      actual.some(
        (panel) => keys.has(panelKey(panel)) && panel.faceIndex !== faces[0]
      )
    )
      return false;
  }
  return true;
};

/** Direct same-source array AC series; never apply another inverter derate. */
export const solarWeatherSeries = (snapshot: PvWattsSnapshot) => ({
  version: 1 as const,
  source: snapshot.source,
  snapshotId: snapshot.snapshotId,
  inputFingerprint: snapshot.inputFingerprint,
  units: snapshot.units,
  annualAcKwh: snapshot.annualAcKwh,
  monthlyAcKwh: [...snapshot.monthlyAcKwh],
});
